// @oasis-permission-inherits: /capcode (capcode:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  CheckSquare,
  Clock,
  ExternalLink,
  GitPullRequest,
  Lightbulb,
  MessageSquare,
} from "lucide-react";
import * as React from "react";

import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";
import { ChatSurface } from "~/modules/cap-code/components/chat-surface";
import {
  fetchInvestigation,
  fetchInvestigationConversations,
  investigationsWsUrl,
} from "~/modules/cap-code/network";
import {
  InvestigationUpdatedPayloadSchema,
  type ConversationEvent,
  type ConversationRecord,
  type InvestigationRecord,
} from "~/modules/cap-code/schemas";

export const Route = createFileRoute("/_dash/capcode/investigations/$investigationId")({
  component: InvestigationRoute,
});

type DetailTab = "chat" | "alerts" | "findings" | "transitions";

function InvestigationRoute() {
  const { investigationId } = Route.useParams();
  const [investigation, setInvestigation] = React.useState<InvestigationRecord | null>(null);
  const [conversations, setConversations] = React.useState<ConversationRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<DetailTab>("chat");
  const investigationEventCursorRef = React.useRef(0);

  const loadInvestigation = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [investigationData, conversationsData] = await Promise.all([
        fetchInvestigation(investigationId),
        fetchInvestigationConversations(investigationId),
      ]);
      setInvestigation(investigationData);
      setConversations(conversationsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load investigation");
    } finally {
      setLoading(false);
    }
  }, [investigationId]);

  React.useEffect(() => {
    void loadInvestigation();
  }, [loadInvestigation]);

  React.useEffect(() => {
    let closedByEffect = false;
    let reconnectAttempt = 0;
    let reconnectTimer: number | undefined;
    let socket: WebSocket | null = null;

    async function connect() {
      const url = await investigationsWsUrl(investigationEventCursorRef.current);
      if (closedByEffect) {
        return;
      }
      socket = new WebSocket(url);

      socket.addEventListener("open", () => {
        reconnectAttempt = 0;
      });

      socket.addEventListener("close", () => {
        if (closedByEffect) {
          return;
        }
        const delay = Math.min(1000 * 2 ** reconnectAttempt, 5000);
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(() => {
          void connect();
        }, delay);
      });

      socket.addEventListener("error", () => {
        socket?.close();
      });

      socket.addEventListener("message", (message) => {
        const payload = JSON.parse(message.data as string) as {
          type: string;
          event?: ConversationEvent;
        };
        if (payload.type !== "event" || !payload.event) {
          return;
        }
        const event = payload.event;
        investigationEventCursorRef.current = Math.max(
          investigationEventCursorRef.current,
          event.cursor ?? 0
        );
        if (event.type === "investigation.updated") {
          const parsed = InvestigationUpdatedPayloadSchema.safeParse(event.payload);
          if (parsed.success && parsed.data.investigation.id === investigationId) {
            setInvestigation(parsed.data.investigation);
            setConversations(parsed.data.conversations);
            setError(null);
          }
        }
      });
    }

    void connect();

    return () => {
      closedByEffect = true;
      if (typeof reconnectTimer !== "undefined") {
        window.clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [investigationId]);

  if (loading && !investigation) {
    return <InvestigationShellSkeleton />;
  }

  if (!investigation) {
    return (
      <div className="h-[calc(100dvh-57px)] overflow-y-auto bg-background p-5">
        <div className="rounded-lg border border-border bg-white p-6">
          <h1 className="text-lg font-semibold">Investigation not found</h1>
          {error && <p className="mt-2 text-sm text-muted-foreground">{error}</p>}
        </div>
      </div>
    );
  }

  const latestAlert = investigation.alertEvents.at(-1);
  const primaryConversation = conversations[0] ?? null;

  return (
    <div className="flex h-[calc(100dvh-57px)] flex-col overflow-hidden bg-background">
      <header className="shrink-0 border-b border-border bg-white">
        <div className="flex min-h-16 items-center justify-between gap-4 px-4 py-3 lg:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-background">
              <AlertTriangle size={17} className={statusIconClass(investigation)} />
            </div>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <h1 className="truncate text-lg font-semibold tracking-tight">
                  {investigation.title}
                </h1>
                <StatusBadge investigation={investigation} />
              </div>
              <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                {investigation.outcome && <Badge>{investigation.outcome}</Badge>}
                {investigation.severity && <Badge>{investigation.severity}</Badge>}
                {investigation.service && (
                  <Badge className="max-w-[280px] truncate">{investigation.service}</Badge>
                )}
                <span>{investigation.alertEvents.length} alerts</span>
                <span>{conversations.length} conversations</span>
                <span>updated {formatDateTime(investigation.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {error && (
        <div className="shrink-0 border-b border-destructive/20 bg-destructive/5 px-5 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <main className="flex min-w-0 flex-1 flex-col border-r border-border">
          <div className="relative shrink-0 border-b border-border bg-white">
            <div className="flex h-11 items-end gap-1 px-4">
              <TabButton active={activeTab === "chat"} onClick={() => setActiveTab("chat")}>
                Chat
              </TabButton>
              <TabButton active={activeTab === "alerts"} onClick={() => setActiveTab("alerts")}>
                Alerts
              </TabButton>
              <TabButton active={activeTab === "findings"} onClick={() => setActiveTab("findings")}>
                Findings
              </TabButton>
              <TabButton
                active={activeTab === "transitions"}
                onClick={() => setActiveTab("transitions")}
              >
                Transitions
              </TabButton>
            </div>
            <div className="pointer-events-none absolute inset-x-0 -bottom-8 z-10 h-8 bg-gradient-to-b from-white to-transparent" />
          </div>

          <div className="min-h-0 flex-1">
            {activeTab === "chat" &&
              (primaryConversation ? (
                <ChatSurface
                  conversationId={primaryConversation.id}
                  mode="embedded"
                  showEventPanel={false}
                  showHistoryButton={false}
                  contentClassName="px-6 py-5"
                />
              ) : (
                <EmptyContent icon={<MessageSquare size={22} />} title="No linked conversation">
                  This investigation does not have a conversation yet.
                </EmptyContent>
              ))}
            {activeTab === "alerts" && <AlertsPane investigation={investigation} />}
            {activeTab === "findings" && <FindingsPane investigation={investigation} />}
            {activeTab === "transitions" && <TransitionsPane investigation={investigation} />}
          </div>
        </main>

        <aside className="hidden w-[380px] shrink-0 flex-col bg-white lg:flex">
          <IncidentInfoPanel
            investigation={investigation}
            conversations={conversations}
            latestAlert={latestAlert}
          />
        </aside>
      </div>
    </div>
  );
}

function InvestigationShellSkeleton() {
  return (
    <div className="flex h-[calc(100dvh-57px)] flex-col overflow-hidden bg-background">
      <div className="h-16 shrink-0 border-b border-border bg-white px-5 py-4">
        <div className="h-5 w-1/2 rounded bg-muted" />
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 border-r border-border p-5">
          <div className="h-6 w-64 rounded bg-muted" />
          <div className="mt-8 space-y-5">
            <div className="h-20 rounded-lg bg-muted" />
            <div className="h-28 rounded-lg bg-muted" />
            <div className="h-24 rounded-lg bg-muted" />
          </div>
        </div>
        <div className="hidden w-[380px] bg-white p-5 lg:block">
          <div className="h-5 w-32 rounded bg-muted" />
          <div className="mt-5 h-40 rounded-lg bg-muted" />
        </div>
      </div>
    </div>
  );
}

function IncidentInfoPanel({
  investigation,
  conversations,
  latestAlert,
}: {
  investigation: InvestigationRecord;
  conversations: ConversationRecord[];
  latestAlert: InvestigationRecord["alertEvents"][number] | undefined;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Incident</h2>
            <p className="text-xs text-muted-foreground">Durable investigation state</p>
          </div>
          <StatusBadge investigation={investigation} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <div className="grid grid-cols-2 gap-2">
          <Metric label="Alerts" value={String(investigation.alertEvents.length)} />
          <Metric label="Actions" value={String(investigation.actions.length)} />
          <Metric label="Findings" value={String(investigation.findings.length)} />
          <Metric label="Updated" value={formatDateTime(investigation.updatedAt)} />
        </div>

        <InfoSection title="Assessment">
          <p className="whitespace-pre-wrap text-sm leading-5 text-muted-foreground">
            {investigation.summary ?? "No current assessment recorded yet."}
          </p>
        </InfoSection>

        {latestAlert && (
          <InfoSection title="Latest Alert">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge>{latestAlert.status}</Badge>
              {latestAlert.routing && (
                <Badge>
                  {latestAlert.routing.action}
                  {latestAlert.routing.confidence !== null
                    ? ` ${Math.round(latestAlert.routing.confidence * 100)}%`
                    : ""}
                </Badge>
              )}
            </div>
            <p className="mt-2 text-sm font-medium">{latestAlert.title}</p>
            <p className="mt-1 whitespace-pre-wrap text-xs leading-5 text-muted-foreground">
              {latestAlert.text}
            </p>
          </InfoSection>
        )}

        <InfoSection title="Linked Conversations">
          {conversations.length === 0 ? (
            <EmptyPanelText>No linked conversations.</EmptyPanelText>
          ) : (
            <div className="flex flex-col gap-1">
              {conversations.map((conversation) => (
                <Link
                  key={conversation.id}
                  to="/capcode/conversations/$conversationId"
                  params={{ conversationId: conversation.id }}
                  className="flex min-w-0 items-center gap-2 rounded-md border border-border px-2.5 py-2 text-sm hover:bg-muted"
                >
                  <MessageSquare size={14} className="shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{conversation.title}</span>
                  <Badge>{conversation.status}</Badge>
                </Link>
              ))}
            </div>
          )}
        </InfoSection>

        <InfoSection title="Findings">
          {investigation.findings.length === 0 ? (
            <EmptyPanelText>No findings recorded.</EmptyPanelText>
          ) : (
            <div className="space-y-3">
              {investigation.findings.slice(0, 4).map((finding) => (
                <div key={finding.id} className="rounded-md border border-border px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Badge>{finding.kind}</Badge>
                    {finding.confidence !== null && (
                      <span className="text-xs text-muted-foreground">
                        {Math.round(finding.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  <p className="mt-2 line-clamp-4 text-sm leading-5">{finding.summary}</p>
                </div>
              ))}
            </div>
          )}
        </InfoSection>

        <InfoSection title="Actions">
          {investigation.actions.length === 0 ? (
            <EmptyPanelText>No actions recorded.</EmptyPanelText>
          ) : (
            <div className="space-y-3">
              {investigation.actions.map((action) => (
                <div key={action.id} className="rounded-md border border-border px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge>{action.type}</Badge>
                    <Badge>{action.status}</Badge>
                  </div>
                  <div className="mt-2 text-sm font-medium">{action.title}</div>
                  {action.url && (
                    <a
                      href={action.url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary"
                    >
                      <GitPullRequest size={13} />
                      Open artifact
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </InfoSection>
      </div>
    </div>
  );
}

function AlertsPane({ investigation }: { investigation: InvestigationRecord }) {
  return (
    <ScrollPane>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-2 px-5 py-5">
        {investigation.alertEvents.map((alert) => (
          <div key={alert.id} className="rounded-md border border-border bg-white px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge>{alert.status}</Badge>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{alert.title}</span>
              <span className="text-xs text-muted-foreground">
                {formatDateTime(alert.timestamp)}
              </span>
              {alert.routing && (
                <Badge>
                  {alert.routing.action}
                  {alert.routing.confidence !== null
                    ? ` ${Math.round(alert.routing.confidence * 100)}%`
                    : ""}
                </Badge>
              )}
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-5 text-muted-foreground">
              {alert.text}
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(alert.labels)
                .slice(0, 8)
                .map(([key, value]) => (
                  <Badge key={`${alert.id}:${key}`} className="max-w-full">
                    <span className="truncate">
                      {key}={value}
                    </span>
                  </Badge>
                ))}
            </div>
          </div>
        ))}
      </div>
    </ScrollPane>
  );
}

function FindingsPane({ investigation }: { investigation: InvestigationRecord }) {
  return (
    <ScrollPane>
      <div className="grid w-full gap-3 px-5 py-5 xl:grid-cols-2">
        <DensePanel title="Findings" icon={<Lightbulb size={15} />}>
          {investigation.findings.length === 0 ? (
            <EmptyPanelText>No findings have been recorded yet.</EmptyPanelText>
          ) : (
            investigation.findings.map((finding) => (
              <div key={finding.id} className="border-b border-border px-3 py-3 last:border-b-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge>{finding.kind}</Badge>
                  {finding.confidence !== null && (
                    <span className="text-xs text-muted-foreground">
                      {Math.round(finding.confidence * 100)}% confidence
                    </span>
                  )}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-5">{finding.summary}</p>
                {finding.evidence.length > 0 && (
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-xs leading-5 text-muted-foreground">
                    {finding.evidence.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </DensePanel>

        <DensePanel title="Actions" icon={<CheckSquare size={15} />}>
          {investigation.actions.length === 0 ? (
            <EmptyPanelText>No actions have been recorded yet.</EmptyPanelText>
          ) : (
            investigation.actions.map((action) => (
              <div key={action.id} className="border-b border-border px-3 py-3 last:border-b-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge>{action.type}</Badge>
                  <Badge>{action.status}</Badge>
                  {action.requiresOnCall && (
                    <Badge className="border-amber-200 bg-amber-50 text-amber-700">
                      on-call todo
                    </Badge>
                  )}
                </div>
                <div className="mt-2 text-sm font-medium">{action.title}</div>
                {action.summary && (
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-muted-foreground">
                    {action.summary}
                  </p>
                )}
                {action.url && (
                  <a
                    href={action.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary"
                  >
                    <ExternalLink size={13} />
                    Open artifact
                  </a>
                )}
              </div>
            ))
          )}
        </DensePanel>
      </div>
    </ScrollPane>
  );
}

function TransitionsPane({ investigation }: { investigation: InvestigationRecord }) {
  return (
    <ScrollPane>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-2 px-5 py-5">
        {investigation.transitions.length === 0 ? (
          <EmptyContent icon={<Activity size={22} />} title="No transitions">
            No state transitions have been recorded yet.
          </EmptyContent>
        ) : (
          investigation.transitions.map((transition) => (
            <div
              key={transition.id}
              className="rounded-md border border-border bg-white px-3 py-2.5"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge>{transition.fromStatus ?? "created"}</Badge>
                <span className="text-xs text-muted-foreground">to</span>
                <Badge>{transition.toStatus}</Badge>
                <Clock size={13} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(transition.createdAt)}
                </span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-5 text-muted-foreground">
                {transition.reason}
              </p>
            </div>
          ))
        )}
      </div>
    </ScrollPane>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "mb-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "border-border bg-background text-foreground shadow-sm"
          : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

function ScrollPane({ children }: { children: React.ReactNode }) {
  return <div className="h-full overflow-y-auto">{children}</div>;
}

function DensePanel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-white">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-muted-foreground">{icon}</span>
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div>{children}</div>
    </section>
  );
}

function EmptyContent({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center">
      <div className="mb-3 flex size-12 items-center justify-center rounded-full border border-border bg-white text-muted-foreground">
        {icon}
      </div>
      <h2 className="text-sm font-semibold">{title}</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function InfoSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 border-t border-border pt-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <div className="text-[11px] font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold">{value}</div>
    </div>
  );
}

function EmptyPanelText({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-muted-foreground">{children}</div>;
}

function StatusBadge({ investigation }: { investigation: InvestigationRecord }) {
  return <Badge className={statusBadgeClass(investigation.status)}>{investigation.status}</Badge>;
}

function statusIconClass(investigation: InvestigationRecord): string {
  if (investigation.status === "resolved" || investigation.status === "ignored") {
    return "text-emerald-600";
  }
  if (investigation.severity === "page" || investigation.severity === "critical") {
    return "text-red-600";
  }
  return "text-amber-600";
}

function statusBadgeClass(status: InvestigationRecord["status"]): string {
  if (status === "resolved" || status === "ignored") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status === "mitigating") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  if (status === "monitoring") {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function formatDateTime(value: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}
